// ============================================================
// GUARD DE SESSÃO — desloga automaticamente por inatividade (60min)
// ou quando o horário atual sai da janela liberada para o perfil.
// ============================================================
import { useEffect, useRef } from 'react';
import { dentroDoHorarioPermitido } from './permissoes';

const TIMEOUT_INATIVIDADE_MS = 60 * 60 * 1000;
const INTERVALO_VERIFICACAO_MS = 60 * 1000;
const EVENTOS_ATIVIDADE = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

// Fonte única do texto exibido na tela de login (App.js) e do rótulo
// usado em LogsAuditoria.jsx — os dois precisam concordar com os motivos
// que este hook realmente emite ('inatividade' | 'horario') mais o motivo
// 'sessao_expirada', emitido não por este hook mas pelo listener SIGNED_OUT
// do próprio App.js quando a sessão cai sem ter passado pelo handleLogout.
export const MOTIVO_LOGOUT_LABELS = {
  inatividade: 'Sessão encerrada por inatividade.',
  horario: 'Sessão encerrada: fora do horário liberado para seu perfil.',
  sessao_expirada: 'Sessão expirada. Foi necessário fazer login novamente.',
};

// ativo: só liga listeners/timer com sessão + perfil já carregados.
// onExpirar(motivo): 'inatividade' | 'horario'.
export function useSessaoGuard(ativo, perfil, onExpirar) {
  const ultimaAtividadeRef = useRef(Date.now());
  const perfilRef = useRef(perfil);
  const onExpirarRef = useRef(onExpirar);
  perfilRef.current = perfil;
  onExpirarRef.current = onExpirar;

  useEffect(() => {
    if (!ativo) return;
    ultimaAtividadeRef.current = Date.now();
    const marcarAtividade = () => { ultimaAtividadeRef.current = Date.now(); };
    EVENTOS_ATIVIDADE.forEach(ev => window.addEventListener(ev, marcarAtividade));

    const verificar = () => {
      if (Date.now() - ultimaAtividadeRef.current > TIMEOUT_INATIVIDADE_MS) {
        onExpirarRef.current('inatividade');
        return;
      }
      if (!dentroDoHorarioPermitido(perfilRef.current)) {
        onExpirarRef.current('horario');
      }
    };

    // Checagem imediata — sem isso, um usuário fora do horário ficaria
    // até 1 minuto logado antes do primeiro tick do intervalo.
    verificar();
    const id = setInterval(verificar, INTERVALO_VERIFICACAO_MS);

    return () => {
      EVENTOS_ATIVIDADE.forEach(ev => window.removeEventListener(ev, marcarAtividade));
      clearInterval(id);
    };
  }, [ativo]);
}
